import { useEffect, useState, type CSSProperties } from "react";
import { Mascot } from "../components/Mascot";
import day from "../results/day.json";
import "./Results.css";

/**
 * The results of Sunday 18 October 2026 (decision 54): the site after the day. Every old link and
 * short address lands here (main.tsx, the Worker's DAY_OVER), the day itself is frozen on the
 * server, and this page reads the saved results (web/src/results/day.json, from
 * tools/recap/public.ts), never the live tournament. Real names only, his call.
 *
 *   /results              the podium, then Board, Rounds, Awards and Numbers
 *   /results/<player id>  one person: their place, titles, numbers and every game they played
 */

type Person = (typeof day.people)[keyof typeof day.people];
type Game = (typeof day.rounds)[number]["games"][number];
type Tab = "board" | "rounds" | "awards" | "numbers";

const people = day.people as Record<string, Person>;
const nameOf = (id: string): string => people[id]?.name ?? id;
const pair = (ids: string[]): string => ids.map(nameOf).join(" & ");
const ORD = (n: number): string => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th")}`;
const TABS: { id: Tab; label: string }[] = [
  { id: "board", label: "Board" },
  { id: "rounds", label: "Rounds" },
  { id: "awards", label: "Awards" },
  { id: "numbers", label: "Numbers" },
];

/** The address for a person's page, or the overview. */
const href = (id?: string): string => (id ? `/results/${encodeURIComponent(id)}` : "/results");

function go(to: string, e?: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }): void {
  if (e?.metaKey || e?.ctrlKey) return;
  e?.preventDefault();
  history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}

function useAddress(): string {
  const [path, setPath] = useState(location.pathname + location.hash);
  useEffect(() => {
    const on = () => setPath(location.pathname + location.hash);
    window.addEventListener("popstate", on);
    window.addEventListener("hashchange", on);
    return () => {
      window.removeEventListener("popstate", on);
      window.removeEventListener("hashchange", on);
    };
  }, []);
  return path;
}

/** A face from the day's own pictures (kept on the server), or the initial when there is none. */
function Face({ id, className }: { id: string; className: string }) {
  const p = people[id];
  const [broken, setBroken] = useState(false);
  if (!p || !p.photo || broken) return <span className={`${className} rs-initial`} aria-hidden="true">{nameOf(id).slice(0, 1)}</span>;
  return <img className={className} src={`/api/photo/${encodeURIComponent(id)}?t=main&v=${p.photo}`} alt="" loading="lazy" onError={() => setBroken(true)} />;
}

function PersonLink({ id, children, className }: { id: string; children: React.ReactNode; className?: string }) {
  return (
    <a className={className} href={href(id)} onClick={(e) => go(href(id), e)}>
      {children}
    </a>
  );
}

function Header() {
  return (
    <header className="rs-top">
      <a href={href()} onClick={(e) => go(href(), e)} className="rs-brand">
        {day.title}
      </a>
      <span>
        <span className="rs-weekday">{day.date.split(" ")[0]} </span>
        {day.date.split(" ").slice(1).join(" ")}
      </span>
    </header>
  );
}

function Podium() {
  const [first, second, third] = day.podium;
  const step = (id: string | undefined, place: number) =>
    id ? (
      <PersonLink id={id} className={`rs-step rs-step-${place}`}>
        <Face id={id} className="rs-step-face" />
        <b className="rs-step-name">{nameOf(id)}</b>
        <span className="rs-step-block">
          <span className="rs-step-place">{ORD(place)}</span>
          <span className="rs-step-avg">{people[id]!.avg.toFixed(1)} a game</span>
        </span>
      </PersonLink>
    ) : null;
  return (
    <section className="rs-podium" aria-label="The podium">
      {step(second, 2)}
      {step(first, 1)}
      {step(third, 3)}
    </section>
  );
}

function Board() {
  return (
    <ol className="rs-board">
      {day.board.map((id) => {
        const p = people[id]!;
        return (
          <li key={id} data-top={p.ranked && p.place !== null && p.place <= 3 ? "1" : undefined}>
            <PersonLink id={id} className="rs-row">
              <b className="rs-row-place">{p.place ?? "-"}</b>
              <Face id={id} className="rs-row-face" />
              <span className="rs-row-name">{p.name}</span>
              <span className="rs-row-record">{p.wins}-{p.losses}</span>
              <em className="rs-row-avg">{p.avg.toFixed(1)}</em>
            </PersonLink>
          </li>
        );
      })}
      <li className="rs-note">Ranked by points a game, then win rate, then points difference. Five games or more for a podium place.</li>
    </ol>
  );
}

/** One game as a line: the winners in bold, the court in its colour. */
function GameLine({ g, me }: { g: Game; me?: string }) {
  const aWon = g.sa > g.sb;
  const side = (ids: string[], won: boolean) => (
    <span className={won ? "rs-won" : "rs-lost"}>
      {ids.map((id, i) => (
        <span key={id}>
          {i ? " & " : ""}
          {id === me ? <b>{nameOf(id)}</b> : <PersonLink id={id}>{nameOf(id)}</PersonLink>}
        </span>
      ))}
    </span>
  );
  return (
    <li className="rs-game">
      <span className="rs-court" style={{ "--court": `var(--bd-court-${g.court})` } as CSSProperties}>{g.court}</span>
      <span className="rs-game-teams">
        {side(g.a, aWon)}
        <b className="rs-score">{g.sa}-{g.sb}</b>
        {side(g.b, !aWon)}
      </span>
    </li>
  );
}

function Rounds() {
  return (
    <div className="rs-rounds">
      {[...day.rounds].reverse().map((r) => (
        <details key={r.no} className="rs-round" open={r.no === day.rounds.length}>
          <summary>
            <b>Round {r.no}</b>
            <span>{r.mode === "mexicano" ? "Mexicano" : "Americano"}, {r.at}</span>
          </summary>
          <ul>
            {r.games.map((g) => <GameLine key={g.court} g={g} />)}
          </ul>
          {r.out.length ? <p className="rs-out">Sitting out: {r.out.map(nameOf).join(", ")}</p> : null}
        </details>
      ))}
    </div>
  );
}

function Awards() {
  return (
    <ul className="rs-awards">
      {day.awards.map((a) => (
        <li key={a.title}>
          <h2>{a.title}</h2>
          <p className="rs-award-who">
            {a.who.map((id, i) => (
              <span key={id}>{i ? ", " : ""}<PersonLink id={id}>{nameOf(id)}</PersonLink></span>
            ))}
          </p>
          <p className="rs-award-what"><Unbroken text={a.detail} /></p>
        </li>
      ))}
    </ul>
  );
}

/** A line with its scores kept whole: "14-12" never breaks at the hyphen. */
function Unbroken({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\d+-\d+)/).map((part, i) => (i % 2 ? <span key={i} className="rs-nowrap">{part}</span> : part))}
    </>
  );
}

function Numbers() {
  const n = day.numbers;
  const tiles: [string, string][] = [
    [String(n.games), "games"],
    [String(n.points), "points"],
    [String(n.rounds), "rounds"],
    [String(n.players), "players"],
    [n.commonScore, `the score of the day, ${n.commonCount} times`],
    [String(n.deuces), "games to deuce"],
  ];
  return (
    <>
      <div className="rs-tiles">
        {tiles.map(([big, what]) => (
          <div key={what}>
            <b>{big}</b>
            <span>{what}</span>
          </div>
        ))}
      </div>
      <ul className="rs-facts">
        <li>First serve {n.first}, last score {n.last}.</li>
        <li>{n.americano} rounds of Americano, then {n.mexicano} of Mexicano.</li>
        {n.longest ? <li>Longest game, {n.longest.minutes} minutes: <Unbroken text={n.longest.line} />.</li> : null}
      </ul>
    </>
  );
}

function Overview({ tab }: { tab: Tab }) {
  return (
    <>
      <section className="rs-hero">
        <div>
          <h1>That's a wrap</h1>
          <p>{day.numbers.games} games, {day.numbers.points} points and one champion. Tap anyone to see their day.</p>
        </div>
        <Mascot pose="crown" className="rs-hero-host" />
      </section>
      <Podium />
      <nav className="rs-tabs" aria-label="Results">
        {TABS.map((t) => (
          <a key={t.id} href={`#${t.id}`} aria-current={t.id === tab ? "page" : undefined}>
            {t.label}
          </a>
        ))}
      </nav>
      {tab === "board" ? <Board /> : tab === "rounds" ? <Rounds /> : tab === "awards" ? <Awards /> : <Numbers />}
    </>
  );
}

function PersonPage({ id }: { id: string }) {
  const p = people[id];
  if (!p) {
    return (
      <section className="rs-missing">
        <p>Nobody by that name played.</p>
        <a href={href()} onClick={(e) => go(href(), e)} className="rs-back">All the results</a>
      </section>
    );
  }
  const games = day.rounds.flatMap((r) => r.games.filter((g) => g.a.includes(id) || g.b.includes(id)).map((g) => ({ r, g })));
  const podium = p.ranked && p.place !== null && p.place <= 3;
  return (
    <article className="rs-person" data-court={p.court ?? 1} data-podium={podium ? "1" : undefined}>
      <a href={href()} onClick={(e) => go(href(), e)} className="rs-back">All the results</a>
      <header className="rs-person-top">
        <Face id={id} className="rs-person-face" />
        <div>
          <h1>{p.name}</h1>
          <p className="rs-person-place">
            <b>{p.place === null ? "-" : ORD(p.place)}</b>
            <span>{podium ? (p.place === 1 ? "Champion" : "On the podium") : `of ${day.board.length}`}{p.ranked ? "" : ", under 5 games"}</span>
          </p>
          <p className="rs-person-record">
            {p.wins} {p.wins === 1 ? "win" : "wins"}, {p.losses} {p.losses === 1 ? "loss" : "losses"}, {p.avg.toFixed(1)} points a game
          </p>
        </div>
      </header>
      {p.wall.length ? (
        <p className="rs-wall"><span>On the wall</span> {p.wall.join(", ")}</p>
      ) : null}
      <ol className="rs-titles">
        {p.titles.map((t, i) => (
          <li key={t.title}>
            <b className="rs-title-n">{i + 1}</b>
            <div>
              <h2>{t.title}</h2>
              <p><Unbroken text={t.line} /></p>
              {t.where ? <em>{t.where}</em> : null}
            </div>
          </li>
        ))}
      </ol>
      <dl className="rs-person-numbers">
        <div><dt>Games</dt><dd>{p.games}</dd></div>
        <div><dt>Points</dt><dd>{p.pointsFor}</dd></div>
        <div><dt>Best run</dt><dd>{p.bestRun}</dd></div>
        <div><dt>People met</dt><dd>{p.peopleMet}</dd></div>
      </dl>
      {p.nemesis ? (
        <p className="rs-nemesis">
          Nemesis: <PersonLink id={p.nemesis.id}>{nameOf(p.nemesis.id)}</PersonLink>, lost to them {p.nemesis.lost} times
        </p>
      ) : null}
      <h2 className="rs-section">Every game</h2>
      <ul className="rs-history">
        {games.map(({ r, g }) => {
          const mine = g.a.includes(id) ? "a" : "b";
          const won = mine === "a" ? g.sa > g.sb : g.sb > g.sa;
          const partner = (mine === "a" ? g.a : g.b).filter((x) => x !== id);
          const them = mine === "a" ? g.b : g.a;
          const [us, theirs] = mine === "a" ? [g.sa, g.sb] : [g.sb, g.sa];
          const best = p.bestWin && p.bestWin.round === r.no && p.bestWin.court === g.court;
          return (
            <li key={r.no} data-won={won ? "1" : undefined}>
              <span className="rs-wl">{won ? "W" : "L"}</span>
              <span className="rs-h-score">{us}-{theirs}</span>
              <span className="rs-h-who">
                with {partner.map((x) => <PersonLink key={x} id={x}>{nameOf(x)}</PersonLink>)} v {them.map((x, i) => <span key={x}>{i ? " & " : ""}<PersonLink id={x}>{nameOf(x)}</PersonLink></span>)}
                {best ? <em> best win</em> : null}
              </span>
              <span className="rs-h-when">R{r.no} <i className="rs-court" style={{ "--court": `var(--bd-court-${g.court})` } as CSSProperties}>{g.court}</i></span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function ResultsView() {
  const address = useAddress();
  const [path, hash] = address.split("#") as [string, string | undefined];
  const id = decodeURIComponent(path.replace(/^\/results\/?/, "").replace(/\/+$/, ""));
  const tab: Tab = TABS.some((t) => t.id === hash) ? (hash as Tab) : "board";
  useEffect(() => {
    document.title = id && people[id] ? `${nameOf(id)}: ${day.title}` : `Results: ${day.title}`;
  }, [id]);
  return (
    <div className="rs">
      <Header />
      <main className="rs-main">{id ? <PersonPage id={id} /> : <Overview tab={tab} />}</main>
    </div>
  );
}
